import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { FormApiService } from '@app/core/services/form-api.service';
import { ManualTimeseriesUploadService } from '@app/api/dmc/api/manualTimeseriesUpload.service';
import { InputGroupComponent } from '@shared/components/input/input-group/input-group.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ToggleComponent } from '@shared/components/input/toggle/toggle.component';
import { AlertService } from '@app/core/services/alert.service';
import { HasUnsavedChanges } from '@app/core/guards/unsaved-changes.guard';

@Component({
  selector: 'app-upload-manual-timeseries-modal',
  imports: [ReactiveFormsModule,  InputGroupComponent, ToggleComponent, TranslatePipe],
  templateUrl: './upload-manual-timeseries-modal.component.html',
  styleUrls: ['./upload-manual-timeseries-modal.component.css']
})
export class UploadManualTimeseriesModalComponent implements OnInit, HasUnsavedChanges {
  public hasUnsavedChanges(): boolean {
    return this.form.dirty;
  }

  public id?: string;

  public form: FormGroup = new FormGroup({
    meteringPointIdentification: new FormControl<string | null>(null, Validators.required),
    csvFile: new FormControl<File | null>(null, Validators.required),
    shouldBeLocked: new FormControl<boolean>(false),
  });

  public uploading = false;
  public isDragging = false;
  public selectedFile: File | null = null;
  public fileError: string | null = null;

  constructor(
    private readonly manualTimeseriesUploadService: ManualTimeseriesUploadService,
    private readonly formApiService: FormApiService,
    private readonly translateService: TranslateService,
    private readonly alertService: AlertService,
  ) {}

  public ngOnInit(): void {
    this.form.get('csvFile')?.setValue(null);
    this.form.get('meteringPointIdentification')?.setValue(this.id ?? null);
    this.uploading = false;
  }

  public onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  public onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  public onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    this.setFile(event.dataTransfer?.files?.item(0) ?? null);
  }

  public onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    this.setFile(input?.files?.item(0) ?? null);
  }

  public removeFile(event: MouseEvent): void {
    event.stopPropagation();
    this.selectedFile = null;
    this.fileError = null;
    this.form.get('csvFile')?.setValue(null);
  }

  private setFile(file: File | null): void {
    this.fileError = null;
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.fileError = this.translateService.instant('meteringpoint.csvupload.dropzone.invalidType');
      return;
    }

    this.selectedFile = file;
    this.form.get('csvFile')?.setValue(file);
  }

  public formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  // ─── Submit ───────────────────────────────────────────────────────────────

  public submit(): void {
    if (this.form.invalid || this.uploading) {
      this.alertService.danger('modal','meteringpoint.csvupload.formInvalid');
      return;
    }

    const file = this.form.get('csvFile')?.value as File | null;
    if (!file) return;

    const wantsToBeLocked = this.form.get('shouldBeLocked')?.value;

    this.uploading = true;

    this.formApiService.submit(
      this.form,
      () => this.manualTimeseriesUploadService.manualTimeseriesUploadPost(file, wantsToBeLocked),
      {
        successMessage: 'meteringpoint.csvupload.uploadsuccess',
        closeModal: true,
        preprocess: () => ({
          meteringPointIdentification: this.id,
          csvFile: file,
          shouldBeLocked: wantsToBeLocked,
        })
      }
    );
  }
}
